import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  extractDigestSearchDocuments,
  searchDocumentAnchor,
  type DigestReport,
  type OverlayItem,
} from '@almanac/contracts';
import {
  DigestView,
  getDigestSections,
  type ItemFeedbackAction,
  type PendingItemFeedback,
} from '@almanac/catalog';
import { useApi } from '../api/useApi';
import { useAlmanacSearch } from '../components/AppShell';
import { DayEpisodes } from '../components/podcast/EpisodeSurfaces';
import { formatLongDate, formatShortTime } from '../lib/format';
import { activeFilterCount, filterDigestReport } from '../lib/search';
import { ALMANAC_DATA_CHANGED } from '../lib/data-events';

interface DigestData {
  report: DigestReport;
  overlay: Record<string, OverlayItem>;
}

/** Adjacent-date navigation targets derived from the full digest list. */
interface DayNav {
  prev: string | null;
  next: string | null;
  today: string | null;
}

function decodeHash(hash: string): string | undefined {
  if (!hash) return undefined;
  try {
    return decodeURIComponent(hash.slice(1));
  } catch {
    return undefined;
  }
}

export function DigestPage() {
  const { date = '' } = useParams();
  const api = useApi();
  const location = useLocation();
  const { query, filters, setLocalEntries, setSectionOptions } = useAlmanacSearch();
  const [data, setData] = useState<DigestData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dates, setDates] = useState<string[] | null>(null);
  const [activeSection, setActiveSection] = useState<string>('');
  const [pendingFeedback, setPendingFeedback] = useState<PendingItemFeedback>({});
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const loadDigest = useCallback(() => {
    setData(null);
    setError(null);
    setFeedbackError(null);
    setPendingFeedback({});
    return api
      .getDigest(date)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [api, date]);

  useEffect(() => {
    void loadDigest();
    const refresh = () => void loadDigest();
    window.addEventListener(ALMANAC_DATA_CHANGED, refresh);
    return () => window.removeEventListener(ALMANAC_DATA_CHANGED, refresh);
  }, [loadDigest]);

  // Fetched once for day-to-day navigation; a failure here is non-fatal —
  // the digest still renders, only the prev/next affordances go quiet.
  useEffect(() => {
    api
      .listDigests()
      .then((r) => setDates(r.digests.map((d) => d.date).sort()))
      .catch(() => setDates([]));
  }, [api]);

  const nav = useMemo<DayNav>(() => {
    if (!dates || dates.length === 0) return { prev: null, next: null, today: null };
    const index = dates.indexOf(date);
    return {
      prev: index > 0 ? (dates[index - 1] ?? null) : null,
      next: index >= 0 && index < dates.length - 1 ? (dates[index + 1] ?? null) : null,
      today: dates[dates.length - 1] ?? null,
    };
  }, [dates, date]);

  const filteredReport = useMemo(
    () => (data ? filterDigestReport(data.report, data.overlay, query, filters) : null),
    [data, filters, query],
  );
  const sections = useMemo(
    () => (filteredReport ? getDigestSections(filteredReport) : []),
    [filteredReport],
  );

  useEffect(() => {
    if (!data) {
      setLocalEntries([]);
      setSectionOptions([]);
      return;
    }
    setSectionOptions(
      getDigestSections(data.report).map((section) => ({ id: section.id, label: section.title })),
    );
    setLocalEntries(
      extractDigestSearchDocuments(data.report).map((document) => ({
        id: document.documentId,
        label: document.title,
        description: document.sectionId
          ? `${data.report.sections[document.sectionId]?.title ?? document.sectionId} · ${document.contentType.replaceAll('_', ' ')}`
          : formatLongDate(document.date),
        keywords: `${document.body} ${document.metadataText} ${document.sourceType ?? ''}`,
        href: `/digests/${document.date}#${searchDocumentAnchor(document.date, document.pointer)}`,
      })),
    );
    return () => {
      setLocalEntries([]);
      setSectionOptions([]);
    };
  }, [data, setLocalEntries, setSectionOptions]);

  useEffect(() => {
    if (!data || !location.hash) return;
    const anchor = decodeHash(location.hash);
    if (!anchor) return;
    const target = document.getElementById(anchor);
    if (!target) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.setTimeout(
      () => target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' }),
      0,
    );
  }, [data, filteredReport, location.hash]);

  // Initialize the active tab, and reset it when navigating between days —
  // the previous digest's section id may not exist in the new section list,
  // which would leave no tab active until the observer fires.
  useEffect(() => {
    if (!sections[0]) return;
    setActiveSection((current) => {
      if (current && sections.some((section) => section.id === current)) return current;
      return sections[0]!.id;
    });
  }, [sections]);

  // Scroll-spy: mark the section nearest the top of the content area (below
  // the sticky header) as active. rootMargin pushes the observation band down
  // past the header and up from the bottom so a single section wins.
  useEffect(() => {
    if (sections.length === 0) return;
    const elements = sections
      .map((section) => document.getElementById(`section-${section.id}`))
      .filter((element): element is HTMLElement => element !== null);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const top = visible[0];
        if (top) setActiveSection(top.target.id.replace(/^section-/u, ''));
      },
      { rootMargin: '-140px 0px -55% 0px', threshold: 0 },
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [sections]);

  function handleTabClick(id: string) {
    setActiveSection(id);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document
      .getElementById(`section-${id}`)
      ?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  async function handleFeedback(itemId: string, action: ItemFeedbackAction) {
    setFeedbackError(null);
    setPendingFeedback((current) => ({ ...current, [itemId]: action }));
    try {
      const { overlay } = await api.sendFeedback({ item_id: itemId, action });
      setData((current) =>
        current
          ? { ...current, overlay: { ...current.overlay, [overlay.item_id]: overlay } }
          : current,
      );
    } catch (feedbackFailure) {
      setFeedbackError(
        feedbackFailure instanceof Error ? feedbackFailure.message : 'Please try again.',
      );
    } finally {
      setPendingFeedback((current) => {
        const next = { ...current };
        delete next[itemId];
        return next;
      });
    }
  }

  const title = data?.report.title ?? (date ? `Daily Digest — ${formatLongDate(date)}` : 'Digest');
  const updatedIso = data?.report.refreshed_at ?? data?.report.generated_at;
  const highlightedAnchor =
    new URLSearchParams(location.search).get('searchHit') ?? decodeHash(location.hash);
  const isFiltering = Boolean(query.trim()) || activeFilterCount(filters) > 0;

  return (
    <div>
      <header className="sticky top-[var(--app-header-height)] z-10 border-b border-border/60 bg-background/90 backdrop-blur">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          {/* flex-wrap lets the day nav drop below the title on narrow
              screens instead of overflowing off-viewport. */}
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="min-w-0">
                <div className="truncate font-display text-lg font-semibold text-foreground">
                  {title}
                </div>
                <div className="text-base text-muted-foreground sm:text-sm">
                  {date}
                  {updatedIso ? ` · updated ${formatShortTime(updatedIso)}` : ''}
                </div>
              </div>
            </div>
            <nav
              className="flex w-full items-center justify-end sm:w-auto"
              aria-label="Day navigation"
            >
              {/* Mobile: one joined ‹ | Today | › stepper. Research and All
                  days are dropped here — both stay reachable from the global
                  Menu in AppShell. */}
              <div className="flex w-full items-stretch divide-x divide-border overflow-hidden rounded-md border border-border sm:hidden">
                <DayStep to={nav.prev ? `/digests/${nav.prev}` : null} label="Previous day">
                  ‹
                </DayStep>
                <DayStep
                  to={nav.today ? `/digests/${nav.today}` : null}
                  active={nav.today !== null && nav.today === date}
                  className="flex-1"
                >
                  Today
                </DayStep>
                <DayStep to={nav.next ? `/digests/${nav.next}` : null} label="Next day">
                  ›
                </DayStep>
              </div>
              {/* sm+: the full control row. */}
              <div className="hidden items-center gap-1.5 sm:flex">
                <Link
                  to="/research"
                  className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                >
                  Research
                </Link>
                <DayNavLink to={nav.prev ? `/digests/${nav.prev}` : null}>‹ Prev</DayNavLink>
                <DayNavLink to="/">All days</DayNavLink>
                <DayNavLink
                  to={nav.today ? `/digests/${nav.today}` : null}
                  active={nav.today !== null && nav.today === date}
                >
                  Today
                </DayNavLink>
                <DayNavLink to={nav.next ? `/digests/${nav.next}` : null}>Next ›</DayNavLink>
              </div>
            </nav>
          </div>
          {sections.length > 0 ? (
            <div className="flex gap-1 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {sections.map((section) => {
                const isActive = section.id === activeSection;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => handleTabClick(section.id)}
                    aria-current={isActive ? 'true' : undefined}
                    className={
                      isActive
                        ? 'flex-none rounded-full bg-primary/15 px-3 py-1 text-sm font-medium text-primary'
                        : 'flex-none rounded-full px-3 py-1 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground'
                    }
                  >
                    {section.title}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        {feedbackError ? (
          <p
            role="alert"
            className="mb-4 rounded-lg bg-danger/10 p-3 text-base text-danger sm:text-sm"
          >
            Could not update the item. {feedbackError}
          </p>
        ) : null}
        {!data && !error ? (
          <div className="space-y-3" aria-hidden>
            <div className="h-6 w-2/3 animate-pulse rounded bg-muted" />
            <div className="h-24 animate-pulse rounded-xl bg-muted" />
            <div className="h-24 animate-pulse rounded-xl bg-muted" />
          </div>
        ) : null}
        {data && date ? <DayEpisodes date={date} className="mb-6" /> : null}
        {data && filteredReport ? (
          <DigestView
            report={filteredReport}
            overlay={data.overlay}
            showTitle={false}
            highlightedAnchor={highlightedAnchor}
            onFeedback={handleFeedback}
            pendingFeedback={pendingFeedback}
          />
        ) : null}
        {data && !filteredReport ? (
          <div className="space-y-2 rounded-xl bg-muted/50 p-6 text-center">
            <p className="text-base font-medium text-foreground">
              {isFiltering ? 'Nothing matches this view' : 'You’re all caught up'}
            </p>
            <p className="text-base text-muted-foreground sm:text-sm">
              {isFiltering
                ? 'Change the query or filters to restore digest content.'
                : 'Completed and dismissed items remain available through the status filter.'}
            </p>
          </div>
        ) : null}
      </main>
    </div>
  );
}

/**
 * A single day-navigation control. Renders an active-styled or plain bordered
 * link when `to` is set, or a muted, non-interactive placeholder when `to` is
 * `null` (e.g. no adjacent digest exists).
 */
function DayNavLink({
  to,
  active = false,
  children,
}: {
  to: string | null;
  active?: boolean;
  children: ReactNode;
}) {
  const base = 'rounded-md border px-3 py-1.5 text-sm';
  if (!to) {
    return (
      <span className={`${base} cursor-not-allowed border-border/60 text-muted-foreground/50`}>
        {children}
      </span>
    );
  }
  if (active) {
    return (
      <Link
        to={to}
        aria-current={active ? 'page' : undefined}
        className={`${base} border-primary/30 bg-primary/15 font-medium text-primary`}
      >
        {children}
      </Link>
    );
  }
  return (
    <Link to={to} className={`${base} border-border text-foreground hover:bg-muted`}>
      {children}
    </Link>
  );
}

/**
 * A single segment of the mobile day-nav stepper. Renders a full-height link
 * (≥44px tap target) styled for its active/idle state, or a muted,
 * non-interactive span when `to` is `null` (no adjacent digest exists).
 */
function DayStep({
  to,
  active = false,
  label,
  className = '',
  children,
}: {
  to: string | null;
  active?: boolean;
  label?: string;
  className?: string;
  children: ReactNode;
}) {
  const base = `flex min-h-11 items-center justify-center px-4 text-sm ${className}`;
  // Icon-only segments (chevrons) pass `label`: hide the glyph from the
  // accessibility tree and expose the label via an sr-only span, which is
  // announced reliably on both the link and the disabled placeholder. The
  // center "Today" segment has no `label` because its visible text is already
  // meaningful, so its children are rendered directly.
  const content = label ? (
    <>
      <span aria-hidden="true">{children}</span>
      <span className="sr-only">{label}</span>
    </>
  ) : (
    children
  );
  if (!to) {
    return (
      <span aria-disabled="true" className={`${base} cursor-not-allowed text-muted-foreground/50`}>
        {content}
      </span>
    );
  }
  const state = active
    ? 'bg-primary/15 font-medium text-primary'
    : 'text-foreground pointer-fine:hover:bg-muted active:bg-muted';
  return (
    <Link to={to} aria-current={active ? 'page' : undefined} className={`${base} ${state}`}>
      {content}
    </Link>
  );
}
